import { useState } from "react";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
} from "reactstrap";
import Image from "next/image";
import _ from "underscore";

//local imports
import classes from "@/styles/info/platform-main.module.css";
import { platform as p } from "@/utils/content";
import { useIsMobile } from "@/hooks/useIsMobile";
import { PluginPlatform } from "@/@types/PluginPlatform";

type Props = {
  platformDetails: PluginPlatform;
};
export default function PlatformMain({ platformDetails }: Props) {
  const content = p.en;

  const isMobile = useIsMobile();
  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState("1");

  //@ts-ignore
  const toggle = (id) => {
    if (open === id) {
      //@ts-ignore
      setOpen();
    } else {
      setOpen(id);
    }
  };

  return (
    <div className={classes.container}>
      <section className="my-5 container">
        <h1 className="text-center h3">{platformDetails?.title}</h1>
        <div className={isMobile ? "text-center" : ""}>
          <Image
            className="mt-3 "
            src={platformDetails?.logoPath}
            alt={platformDetails?.logoPath}
            height={75}
            width={230}
          />
        </div>
        <div className={isMobile ? "" : "w-75"}>
          <p>
            {content.with}&nbsp;
            <span className="fw-bold">{content.shipVista}</span>
          </p>
          <p>{platformDetails?.withShipvista}</p>
          {platformDetails?.note && (
            <p className="fw-bold">
              {content.note}:&nbsp;
              <span className="fst-italic">{platformDetails?.note}</span>
            </p>
          )}
          {!_.isEmpty(platformDetails?.desc) && (
            <>
              <p>{platformDetails?.desc?.title}</p>
              <ul>
                {platformDetails?.desc?.list?.map((l: any, i: number) => {
                  return (
                    <li key={i}>
                      <span className="fw-bold">{l.title}</span>
                      &nbsp;-&nbsp;{l.list}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </section>
      <section className="my-5 container">
        <div className="d-flex flex-wrap">
          <Button
            className={tab === 0 ? classes.tabBtnActive : classes.tabBtn}
            onClick={() => setTab(0)}
          >
            {content.description}
          </Button>
          <Button
            className={tab === 1 ? classes.tabBtnActive : classes.tabBtn}
            onClick={() => setTab(1)}
          >
            {content.changeLog}
          </Button>
          <Button
            className={tab === 2 ? classes.tabBtnActive : classes.tabBtn}
            onClick={() => setTab(2)}
          >
            {content.faqs}
          </Button>
        </div>
        <div className={classes.tabBody}>
          {tab === 0 && (
            <div>
              <h4>{content.description}</h4>
              <h5>{platformDetails?.description?.title}</h5>
              <p>{platformDetails?.description?.description}</p>
              {platformDetails?.description?.whyChoseShipvista && (
                <>
                  <h5>{content.whyShipvista}</h5>
                  <p>{platformDetails?.description?.whyChoseShipvista}</p>
                </>
              )}
              {platformDetails?.description?.mainFeatures && (
                <>
                  <h5>{content.mainFeatures}</h5>
                  <ul>
                    {platformDetails?.description.mainFeatures?.map(
                      (f: any, i: number) => {
                        return <li key={i}>{f}</li>;
                      }
                    )}
                  </ul>
                </>
              )}
              {platformDetails?.description?.pluginWorks && (
                <>
                  <h5>{content.pluginWorks}</h5>
                  <p>{platformDetails?.description.pluginWorks?.desc}</p>
                  <ul>
                    {platformDetails?.description.pluginWorks.list?.map(
                      (f: any, i: number) => {
                        return <li key={i}>{f}</li>;
                      }
                    )}
                  </ul>
                  <p>
                    {content.note}:&nbsp;
                    <span className="fst-italic">
                      {platformDetails?.description.pluginWorks?.note}
                    </span>
                  </p>
                </>
              )}
              {platformDetails?.description?.whyNeedShipvista && (
                <>
                  <h5>{content.whyShipvistaAccount}</h5>
                  <ul>
                    {platformDetails?.description?.whyNeedShipvista?.map(
                      (f: any, i: number) => {
                        return <li key={i}>{f}</li>;
                      }
                    )}
                  </ul>
                </>
              )}
              {platformDetails?.description?.needHelp && (
                <>
                  <h5>{content.needHelp}</h5>
                  <p>{platformDetails?.description?.needHelp}</p>
                </>
              )}
              <div className="text-center my-5">
                <h4>{content.screenshots}</h4>
                {platformDetails?.description?.screenShots &&
                  platformDetails?.description?.screenShots.map(
                    (s: any, i: number) => {
                      return (
                        <div className="my-5" key={i}>
                          <h5 className={classes.screenshotHeading}>
                            {s.title}
                          </h5>
                          <Image
                            className={classes.screenshotImg}
                            src={s.img}
                            alt={s.img}
                            height={500}
                            width={500}
                          />
                        </div>
                      );
                    }
                  )}
              </div>
            </div>
          )}
          {tab === 1 && (
            <div>
              <h4>{content.changeLog}</h4>
              {platformDetails?.changeLog?.versions?.map(
                (c: any, i: number) => {
                  return (
                    <div key={i}>
                      <h5>{c.name}</h5>
                      {c.newFeatures && (
                        <>
                          <p>[New Features]</p>
                          {c.newFeatures?.map((im: any, i: number) => {
                            return (
                              <p className="my-1" key={i}>
                                {im}
                              </p>
                            );
                          })}
                        </>
                      )}
                      {c.release && (
                        <>
                          <p>[Release]</p>
                          {c.release?.map((im: any, i: number) => {
                            return (
                              <p className="my-1" key={i}>
                                {im}
                              </p>
                            );
                          })}
                        </>
                      )}
                      {c.improvements && (
                        <>
                          <p>[Improvements]</p>
                          {c.improvements?.map((im: any, i: number) => {
                            return (
                              <p className="my-1" key={i}>
                                {im}
                              </p>
                            );
                          })}
                        </>
                      )}
                    </div>
                  );
                }
              )}
            </div>
          )}
          {tab === 2 && (
            <div>
              <h4>{content.faqs}</h4>
              {/*@ts-ignore*/}
              <Accordion open={open} toggle={toggle}>
                {platformDetails?.faq?.map((f: any, i: number) => {
                  return (
                    <AccordionItem key={i} className="rounded-0">
                      <AccordionHeader targetId={(i + 1).toString()}>
                        {f.title}
                      </AccordionHeader>
                      <AccordionBody accordionId={(i + 1).toString()}>
                        <p>{f.desc}</p>
                      </AccordionBody>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
